"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  CreditCard,
  ImageOff,
  StickyNote,
  X,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  faDateTime,
  faToman,
  formatCard,
} from "@/lib/format";
import type { PaymentWithRelations } from "./types";
import ApproveDialog from "./ApproveDialog";

type Props = { onRefresh: () => void };

export default function PendingPayments({ onRefresh }: Props) {
  const [payments, setPayments] = React.useState<PaymentWithRelations[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<PaymentWithRelations | null>(
    null,
  );
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [rejectTarget, setRejectTarget] =
    React.useState<PaymentWithRelations | null>(null);
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [rejectNote, setRejectNote] = React.useState("");
  const [rejecting, setRejecting] = React.useState(false);

  const loadPayments = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/payments?status=PENDING", {
        cache: "no-store",
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در دریافت پرداخت‌ها");
      }
      setPayments(data.payments ?? []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در دریافت پرداخت‌ها";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  function openApprove(p: PaymentWithRelations) {
    setSelected(p);
    setDialogOpen(true);
  }

  function openReject(p: PaymentWithRelations) {
    setRejectTarget(p);
    setRejectNote("");
    setRejectOpen(true);
  }

  async function doReject() {
    if (!rejectTarget) return;
    if (!rejectNote.trim()) {
      toast.error("برای رد پرداخت، دلیل را وارد کنید.");
      return;
    }
    setRejecting(true);
    try {
      const res = await fetch("/api/admin/payments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: rejectTarget.id,
          action: "reject",
          adminNote: rejectNote.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا در رد پرداخت");
      toast.success("پرداخت رد شد.");
      setRejectOpen(false);
      loadPayments();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در رد پرداخت";
      toast.error(msg);
    } finally {
      setRejecting(false);
    }
  }

  function handleApproveDone() {
    loadPayments();
    onRefresh();
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-40 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="rounded-2xl border-destructive/40 bg-destructive/5">
        <CardContent className="py-8 text-center text-sm text-destructive">
          {error}
        </CardContent>
      </Card>
    );
  }

  if (payments.length === 0) {
    return (
      <Card className="rounded-2xl border-primary/30 bg-primary/5">
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <div className="flex size-14 items-center justify-center rounded-full bg-primary/15">
            <CheckCircle2 className="size-7 text-primary" />
          </div>
          <div className="text-base font-medium">همه‌چیز مرتب است!</div>
          <p className="text-sm text-muted-foreground">
            پرداخت در انتظاری وجود ندارد ✓
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {payments.map((p) => (
        <Card
          key={p.id}
          className="overflow-hidden rounded-2xl border-border/60 shadow-sm transition-shadow hover:shadow-md"
        >
          <CardHeader className="border-b bg-muted/30 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <CreditCard className="size-5" />
                </span>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{p.user.name}</span>
                    <span dir="ltr" className="text-xs text-muted-foreground">
                      {p.user.phone}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {faDateTime(p.createdAt)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-left">
                  <div className="text-xs text-muted-foreground">مبلغ</div>
                  <div className="font-bold text-primary">
                    {faToman(p.amountToman)}
                  </div>
                </div>
                <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400">
                  در انتظار
                </Badge>
              </div>
            </div>
          </CardHeader>

          <CardContent className="grid grid-cols-1 gap-4 pt-4 md:grid-cols-[2fr_1fr]">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InfoItem label="طرح" value={p.plan.name} />
              <InfoItem
                label="کارت فرستنده"
                value={p.senderCard ? formatCard(p.senderCard) : "وارد نشده"}
                mono
              />
              {p.trackingCode && (
                <InfoItem label="کد پیگیری" value={p.trackingCode} mono />
              )}
              {p.note && (
                <div className="sm:col-span-2 flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-sm">
                  <StickyNote className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <span className="text-muted-foreground">{p.note}</span>
                </div>
              )}
            </div>

            {p.receiptImage ? (
              <a
                href={p.receiptImage}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block overflow-hidden rounded-xl border bg-muted/40"
              >
                <img
                  src={p.receiptImage}
                  alt="تصویر رسید"
                  className="h-32 w-full object-cover transition-transform group-hover:scale-105"
                />
                <span className="absolute bottom-1 right-1 rounded bg-black/60 px-2 py-0.5 text-[10px] text-white">
                  مشاهده بزرگ
                </span>
              </a>
            ) : (
              <div className="flex h-32 flex-col items-center justify-center gap-2 rounded-xl border bg-muted/40 text-xs text-muted-foreground">
                <ImageOff className="size-6" />
                تصویر رسید ثبت نشده
              </div>
            )}

            <div className="flex flex-col gap-2 sm:col-span-2 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => openReject(p)}
              >
                <X className="size-4" />
                رد
              </Button>
              <Button
                className="bg-primary hover:bg-primary/90"
                onClick={() => openApprove(p)}
              >
                <CheckCircle2 className="size-4" />
                تأیید و فعال‌سازی
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      <ApproveDialog
        payment={selected}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onDone={handleApproveDone}
      />

      <Dialog open={rejectOpen} onOpenChange={(o) => !o && setRejectOpen(false)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <X className="size-5" />
              رد پرداخت
            </DialogTitle>
            <DialogDescription>
              {rejectTarget?.user.name
                ? `پرداخت ${rejectTarget.user.name} رد خواهد شد.`
                : "این پرداخت رد خواهد شد."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="reject-reason">دلیل رد *</Label>
            <Textarea
              id="reject-reason"
              placeholder="مثلاً: رسید نامعتبر است / مبلغ ناقص است."
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              className="min-h-24 text-sm"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectOpen(false)}
              disabled={rejecting}
            >
              انصراف
            </Button>
            <Button
              variant="destructive"
              onClick={doReject}
              disabled={rejecting}
            >
              {rejecting ? "در حال رد..." : "تأیید رد"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        dir={mono ? "ltr" : undefined}
        className={`text-sm ${mono ? "font-mono text-left" : "font-medium"}`}
      >
        {value}
      </span>
    </div>
  );
}
